/**
 * Public digest unsubscribe page (external recipients of the weekly org
 * digest or the admins digest; the token says which, and the copy follows).
 *
 * Reached from the "Unsubscribe" link in the digest email. The
 * viewer has no account: the signed token in the URL is the entire grant.
 * The page is deliberately two-step — load info via GET (read-only, safe
 * for mail-scanner prefetch), then unsubscribe only on the explicit button
 * POST.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDigestUnsubscribeInfo, postDigestUnsubscribe } from '../../api/digest';

type PageState = 'loading' | 'confirm' | 'already' | 'done' | 'invalid' | 'error';

export default function DigestUnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [listLabel, setListLabel] = useState('digest');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    let cancelled = false;
    getDigestUnsubscribeInfo(token)
      .then((info) => {
        if (cancelled) return;
        setOrgName(info.orgName);
        setEmail(info.email);
        setListLabel(info.listLabel || 'digest');
        setState(info.subscribed ? 'confirm' : 'already');
      })
      .catch(() => {
        if (!cancelled) setState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      await postDigestUnsubscribe(token);
      setState('done');
    } catch {
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Email Digest</h1>

        {state === 'loading' && <p className="text-gray-500">Loading…</p>}

        {state === 'invalid' && (
          <p className="text-gray-600">
            This unsubscribe link is not valid. If you keep receiving digest emails you don't
            want, contact the organization that sends them.
          </p>
        )}

        {state === 'confirm' && (
          <>
            <p className="text-gray-600 mb-6">
              Stop sending the {listLabel} from <strong>{orgName}</strong> to{' '}
              <strong>{email}</strong>?
            </p>
            <button
              type="button"
              onClick={handleUnsubscribe}
              disabled={submitting}
              className="px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Unsubscribing…' : 'Unsubscribe'}
            </button>
          </>
        )}

        {state === 'already' && (
          <p className="text-gray-600">
            <strong>{email}</strong> is no longer on {orgName}'s {listLabel} list. No further
            emails will be sent.
          </p>
        )}

        {state === 'done' && (
          <p className="text-gray-600">
            Done. <strong>{email}</strong> has been unsubscribed from {orgName}'s{' '}
            {listLabel}. The organization's admins have been notified.
          </p>
        )}

        {state === 'error' && (
          <p className="text-gray-600">
            Something went wrong. Please try the link again in a few minutes.
          </p>
        )}
      </div>
    </div>
  );
}
